<template>
  <Paginator
    v-show="is_visible"
    v-bind="$attrs"
    :first="(pagination_safe.page - 1) * pagination_safe.per_page"
    :rows="pagination_safe.per_page"
    :totalRecords="pagination_safe.total_count"
    :rowsPerPageOptions="rows_per_page_options"
    :showCurrentPageReport="show_total_count"
    :currentPageReportTemplate="total_count_template"
    :template="paginator_template"
    :pt="paginator_pt"
    @page="handlePage"
  >
    <template
      #container="{ first, last, page, pageCount, prevPageCallback, nextPageCallback, totalRecords }"
      v-if="mq.isMobile"
    >
      <div class="flex w-full items-center justify-between gap-4 bg-transparent px-2 py-1">
        <ButtonTertiary
          action="previous"
          class="p-2"
          :hide_text="true"
          :aria-label="t('pagination.previous')"
          :is_disabled="page === 0"
          @onClick="prevPageCallback"
        />
        <div class="text-color font-medium">
          <span class="hidden sm:block">{{ t('pagination.report', { first, last, total: totalRecords }) }}</span>
          <span class="block sm:hidden">{{ t('pagination.page_report', { page: page + 1, total: pageCount }) }}</span>
        </div>
        <ButtonTertiary
          action="next"
          class="p-2"
          :hide_text="true"
          :aria-label="t('pagination.next')"
          :is_disabled="page === pageCount - 1"
          @onClick="nextPageCallback"
        />
      </div>
    </template>
  </Paginator>
</template>

<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMqService } from '@/plugins/mq';
import { DEFAULT_PER_PAGE, DEFAULT_ROWS_PER_PAGE_OPTIONS } from '@/app/data/pagination_constants';

const props = defineProps({
  pagination: { type: Object, required: true },
  rowsPerPageOptions: { type: Array, default: null },
  hideRowsPerPageOptions: { type: Boolean },
  loading: { type: Boolean },
  showTotalCount: { type: Boolean, default: true },
});

const emit = defineEmits(['update:pagination', 'change']);

const mq = useMqService();

const pagination_safe = computed(() => ({
  page: props.pagination.page || 1,
  per_page: props.pagination.per_page || DEFAULT_PER_PAGE,
  total_pages: props.pagination.total_pages || 0,
  total_count: props.pagination.total_count || 0,
}));

const { t } = useI18n();

const rows_per_page_options = computed(() => {
  if (mq.isMobile || props.hideRowsPerPageOptions) {
    return [];
  }

  const base_options = props.rowsPerPageOptions || DEFAULT_ROWS_PER_PAGE_OPTIONS;
  const options = new Set([...base_options, pagination_safe.value.per_page]);
  return [...options].sort((a, b) => a - b);
});

const rows_per_page_reducible = computed(() => {
  const options = rows_per_page_options.value;
  return options.length > 0 && pagination_safe.value.total_count > options[0];
});

const has_page_links = computed(() => pagination_safe.value.total_pages > 1);

const show_total_count = computed(() => props.showTotalCount);
const total_count_template = computed(() => {
  if (mq.isMobile) {
    return '{currentPage}';
  }

  if (pagination_safe.value.total_pages > 2) {
    return t('pagination.report', {
      first: '{first}',
      last: '{last}',
      total: '{totalRecords}',
    });
  }

  return t('pagination.total', { total: '{totalRecords}' });
});
const paginator_template = computed(() => {
  if (has_page_links.value) {
    if (mq.isMobile) {
      return 'FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink';
    }

    const rows_per_page_dropdown = rows_per_page_options.value.length > 0 ? 'RowsPerPageDropdown ' : '';
    return `FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink ${rows_per_page_dropdown}CurrentPageReport`;
  }

  if (rows_per_page_reducible.value) {
    return show_total_count.value ? 'RowsPerPageDropdown CurrentPageReport' : 'RowsPerPageDropdown';
  }

  return 'CurrentPageReport';
});

const paginator_pt = computed(() => ({
  root: {
    // Transparent, unlike the paginator inside a DataTable: this one sits under a list of cards,
    // where a white bar of its own reads as a stray extra card.
    class: mq.isMobile
      ? 'flex flex-wrap items-center justify-center gap-x-1 gap-y-2 bg-transparent'
      : [
          'flex flex-wrap items-center gap-x-2 gap-y-2 bg-transparent',
          has_page_links.value ? "after:content-[''] after:basis-full after:order-2" : '',
        ],
  },
  first: { class: 'order-1' },
  prev: { class: 'order-1' },
  pages: { class: 'order-1' },
  next: { class: mq.isMobile ? 'order-3' : 'order-1' },
  last: { class: mq.isMobile ? 'order-3' : 'order-1' },
  current: { class: 'order-2' },
  rowsPerPageDropdown: { class: 'order-3' },
}));

const is_visible = computed(() => {
  if (props.loading) {
    return false;
  }

  return (
    pagination_safe.value.total_pages > 1 ||
    rows_per_page_reducible.value ||
    (show_total_count.value && pagination_safe.value.total_count > 0)
  );
});

const handlePage = (event) => {
  const pagination_next = {
    ...props.pagination,
    page: event.page + 1,
    per_page: event.rows,
  };

  emit('update:pagination', pagination_next);
  emit('change', pagination_next);
};
</script>
